import { useCallback } from 'react';
import { useAssessmentsContext } from './useAssessmentsContext';

/**
 * Hook to detect if assessment form values have changed since last save/load.
 * Uses React Hook Form's built-in `formState.isDirty` for optimal performance.
 *
 * @returns Object with:
 *   - hasFormChanged: boolean indicating if any field has been modified
 *   - shouldAutoSave: function that returns true if auto-save should be called
 *   - resetDirtyState: function to reset the form's dirty state after successful save
 *
 * @example
 * ```ts
 * const { shouldAutoSave, resetDirtyState } = useAssessmentsFormChangeDetection();
 *
 * const handlePageChange = () => {
 *   if (shouldAutoSave()) {
 *     await autoSaveAnswers(getValues());
 *     resetDirtyState(); // Gets fresh values internally
 *   }
 * };
 * ```
 */
export const useAssessmentsFormChangeDetection = () => {
  const {
    formMethods: { formState, reset, getValues },
  } = useAssessmentsContext();

  /**
   * Checks if the form has any unsaved changes.
   * Uses React Hook Form's formState.isDirty which tracks modifications
   * from the form's default values.
   */
  const hasFormChanged = formState.isDirty;

  /**
   * Determines if auto-save should be called.
   * Returns true only if there are unsaved changes.
   *
   * @returns boolean - true if auto-save should be triggered
   */
  const shouldAutoSave = useCallback((): boolean => formState.isDirty, [formState.isDirty]);

  /**
   * Resets the form's dirty state after a successful save.
   * This updates the form's default values to the current values,
   * effectively marking the form as "clean" without losing data.
   *
   * Call this after a successful auto-save to prevent redundant saves.
   *
   * Note: Always gets fresh values to avoid race conditions where user
   * input could be overwritten if they made changes during the save operation.
   */
  const resetDirtyState = useCallback(() => {
    // Always get fresh values to avoid overwriting user input
    // if they made changes while save was in progress
    const valuesToReset = getValues();
    // Reset form with current values as both the form values AND new defaults
    // This marks the form as not dirty since values === defaults
    reset(valuesToReset, {
      keepErrors: false,
      keepDirty: false,
    });
  }, [reset, getValues]);

  return {
    hasFormChanged,
    shouldAutoSave,
    resetDirtyState,
  };
};
